// Shared by AuditActionBadge.jsx and AdminAuditPage.jsx - one place
// mapping the backend's AdminAuditAction enum (models/admin_audit_log.py)
// to a human label/badge variant, and formatting the small metadata
// object a handful of actions carry ({from, to} for role/status
// changes, {reason} for a soft delete, {name, subscriptions} for a
// pricing action).

export const AUDIT_ACTION_CONFIG = {
  deployment_drain_changed: { label: 'Server Update Mode Changed', variant: 'question' },
  user_disabled: { label: 'User Disabled', variant: 'paused' },
  user_enabled: { label: 'User Enabled', variant: 'listening' },
  user_role_changed: { label: 'Role Changed', variant: 'question' },
  user_sessions_revoked: { label: 'Sessions Revoked', variant: 'reconnecting' },
  user_soft_deleted: { label: 'User Moved to Trash', variant: 'disconnected' },
  user_restored: { label: 'User Restored', variant: 'listening' },
  meeting_content_viewed: { label: 'Content Viewed', variant: 'neutral' },
  meeting_soft_deleted: { label: 'Meeting Moved to Trash', variant: 'disconnected' },
  meeting_restored: { label: 'Meeting Restored', variant: 'listening' },
  pricing_plan_created: { label: 'Plan Created', variant: 'listening' },
  pricing_plan_updated: { label: 'Plan Updated', variant: 'question' },
  pricing_plan_soft_deleted: { label: 'Plan Moved to Trash', variant: 'disconnected' },
  pricing_plan_restored: { label: 'Plan Restored', variant: 'listening' },
  // Retired on the backend (plans are soft-deleted now), but rows
  // written before that change still carry it and must stay readable.
  pricing_plan_deleted: { label: 'Plan Deleted', variant: 'disconnected' },
  api_credit_settings_updated: { label: 'API Credit Settings Updated', variant: 'question' },
  api_credit_snapshot_recorded: { label: 'API Balance Confirmed', variant: 'listening' },
  api_credit_adjustment_recorded: { label: 'API Credit Adjustment', variant: 'question' },
  api_credit_incident_resolved: { label: 'API Credit Alert Resolved', variant: 'listening' },
  api_credit_check_requested: { label: 'API Credit Check', variant: 'neutral' },
  api_credit_test_email_sent: { label: 'API Credit Test Email', variant: 'neutral' },
};

export function formatAuditAction(action) {
  return AUDIT_ACTION_CONFIG[action]?.label || action;
}

// Renders {from: 'user', to: 'admin'} as "USER → ADMIN" rather than raw
// JSON. Falls back to a compact "key: value" list for any metadata
// shape a future audit action might introduce, so this never needs a
// matching code change to stay readable - only raw JSON.stringify is
// avoided, never invented data.
export function formatAuditMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return null;

  const entries = Object.entries(metadata);
  if (entries.length === 0) return null;

  if (
    typeof metadata.from !== 'undefined' &&
    typeof metadata.to !== 'undefined' &&
    entries.length === 2
  ) {
    return `${String(metadata.from).toUpperCase()} → ${String(metadata.to).toUpperCase()}`;
  }

  return entries.map(([key, value]) => `${key}: ${value}`).join(', ');
}
